// DataroomHomePage.js - Enhanced home page with role-based access control
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FolderPlus, Layout, UserPlus, Shield, Users, Folder, File, CreditCard, Wand2 } from 'lucide-react';
import { formatDate } from '../utils/dateUtils';
import { getProjectSlug } from '../utils/slugUtils';

const DataroomHomePage = ({ projects, onCreateProject, onSelectTemplate, onProjectClick, userRole }) => {
  const navigate = useNavigate();
  
  const templates = [
    { id: 1, name: 'M&A Due Diligence', description: 'Complete due diligence folder structure' },
    { id: 2, name: 'Capital Raising', description: 'Investor presentation and documents' },
    { id: 3, name: 'Board Meeting', description: 'Board papers and reports' },
    { id: 4, name: 'Performance Reporting', description: 'Monthly/quarterly reports' },
    { id: 5, name: 'Legal Documents', description: 'Contracts and legal files' }
  ];

  // Check if user can create datarooms (Creator or Super Admin)
  const canCreateDataroom = userRole === 'creator' || userRole === 'super_admin';
  
  // Debug logging
  console.log('DataroomHomePage - userRole:', userRole, 'canCreateDataroom:', canCreateDataroom, 'projects:', projects);
  
  // Note: Redirect logic removed to avoid ESLint issues

  // Handle subscription redirect
  const handleGoToSubscription = () => {
    navigate('/my-account/subscriptions');
  };

  return (
    <div className="flex-1">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My DealRooms</h1>
        {canCreateDataroom && (
          <button 
            onClick={onCreateProject}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Create New DealRoom
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content - Projects */}
        <div className="lg:col-span-3">
          {projects && projects.length > 0 ? (
            <>
              {/* Setup Wizard Trigger - Always show for debugging */}
              {console.log('Debug - canCreateDataroom:', canCreateDataroom, 'userRole:', userRole, 'projects.length:', projects.length)}
              
              
              <div className="grid grid-cols-1 gap-6">
                {projects.map((project, index) => (
                  <div key={project.id || `project-${index}`} className="space-y-3">
                    <ProjectCard 
                      project={project} 
                      onProjectClick={onProjectClick}
                    />
              {canCreateDataroom && (
                <div className="flex justify-center">
                  <button
                    onClick={() => {
                      const slug = getProjectSlug(project);
                      navigate(`/dealroom/wizard/${slug}`);
                    }}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Setup Wizard
                  </button>
                </div>
              )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <EmptyState 
              canCreateDataroom={canCreateDataroom} 
              onCreateProject={onCreateProject}
              onGoToSubscription={handleGoToSubscription}
            />
          )}
        </div>

        {/* Right Sidebar - Get Started - Only show for Creators/Super Admins */}
        {canCreateDataroom && (
          <div className="lg:col-span-1">
            <GetStartedSidebar 
              onCreateProject={onCreateProject}
              onSelectTemplate={onSelectTemplate}
              templates={templates}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Empty state component with role-based messaging
const EmptyState = ({ canCreateDataroom, onCreateProject, onGoToSubscription }) => {
  if (canCreateDataroom) {
    // Message for Creators/Super Admins
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <Folder className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No DealRooms yet</h3>
        <p className="text-gray-500 mb-4">Create your first DealRoom to get started</p>
        <button
          onClick={onCreateProject}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create First DealRoom
        </button>
      </div>
    );
  } else {
    // Message for Viewers and Support
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <Folder className="mx-auto h-12 w-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No DealRooms Available</h3>
        <p className="text-gray-600 mb-2">You have not been invited to a DealRoom yet.</p>
        <p className="text-gray-500 mb-6">Do you want to create a DealRoom?</p>
        <button
          onClick={onGoToSubscription}
          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          <CreditCard className="h-5 w-5 mr-2" />
          Go to Subscriptions
        </button>
        <p className="text-sm text-gray-400 mt-4">Subscribe to create and manage datarooms</p>
      </div>
    );
  }
};

// Project card component
const ProjectCard = ({ project, onProjectClick }) => {
  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      archived: 'bg-gray-100 text-gray-800',
      draft: 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || colors.active;
  };

  return (
    <div 
      onClick={() => onProjectClick(project.id)}
      className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer border-l-4"
      style={{ borderLeftColor: project.color || '#3B82F6' }}
    >
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {project.project_name}
            </h3>
            <p className="text-sm text-gray-600 line-clamp-2">
              {project.description || 'No description'}
            </p>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project.status)}`}>
            Active
          </span>
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <div className="flex items-center">
              <File className="h-4 w-4 mr-1" />
              <span>{project.file_count || 0} files</span>
            </div>
            <div className="flex items-center">
              <Users className="h-4 w-4 mr-1" />
              <span>{project.user_count || 0} users</span>
            </div>
          </div>
          <span className="text-xs text-gray-400">
            {formatDate(project.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
};

// Get Started sidebar component
const GetStartedSidebar = ({ onCreateProject, onSelectTemplate, templates }) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Get Started</h2>
      
      <div className="space-y-4 mb-6">
        <QuickActionButton 
          icon={Plus} 
          title="Create Blank DealRoom" 
          description="Start from scratch"
          onClick={onCreateProject}
        />
        <QuickActionButton 
          icon={Layout} 
          title="Use Template" 
          description="Quick setup with pre-built structure"
          onClick={onSelectTemplate}
        />
      </div>

      <div className="pt-4 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Popular Templates</h3>
        <div className="space-y-2">
          {templates.slice(0, 3).map(template => (
            <button
              key={template.id}
              onClick={onSelectTemplate}
              className="w-full text-left p-2 rounded hover:bg-gray-50 transition-colors"
            >
              <div className="text-sm font-medium text-gray-900">{template.name}</div>
              <div className="text-xs text-gray-500">{template.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Quick Links</h3>
        <div className="space-y-2">
          <QuickActionLink icon={UserPlus} text="Invite Users" />
          <QuickActionLink icon={Shield} text="Manage Permissions" />
          <QuickActionLink icon={FolderPlus} text="Upload Documents" />
        </div>
      </div>
    </div>
  );
};

// Quick action button component
const QuickActionButton = ({ icon: Icon, title, description, onClick }) => {
  return (
    <button 
      onClick={onClick}
      className="w-full flex items-start p-3 rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors group"
    >
      <Icon className="h-5 w-5 text-gray-400 group-hover:text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
      <div className="text-left">
        <div className="font-medium text-gray-900 group-hover:text-blue-600">{title}</div>
        <div className="text-xs text-gray-500">{description}</div>
      </div>
    </button>
  );
};

// Quick action link component
const QuickActionLink = ({ icon: Icon, text }) => {
  return (
    <div className="flex items-center text-sm text-gray-600 hover:text-blue-600 cursor-pointer">
      <Icon className="h-4 w-4 mr-2" />
      <span>{text}</span>
    </div>
  );
};

export default DataroomHomePage;